import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';

import bulkUploadActions, {
  BULK_UPLOAD_CHOOSE_CANDIDATE_MATCH_RESULT,
  BULK_UPLOAD_DECIDE_SHEET_PATIENTS_RESULT,
  BULK_UPLOAD_GET_CANDIDATE_MATCHES_RESULT,
  BULK_UPLOAD_GET_SHEET_PATIENTS_RESULT,
  BULK_UPLOAD_RETRY_CANDIDATE_RESULT,
} from './actions';
import Table3, { columnHelper } from '../../../../containers/Table/Table3';
import Strings from '../../../../Strings';
import Multiselect from '../../../../components/Select/Multiselect';
import SimpleModal from '../../../../containers/Modal/SimpleModal';
import { notificationActions } from '../../../../components/Notification/redux/actions';
import { openModalAction } from '../../../../actions/modal';
import NewConfirmationModal from '../../../../modals/NewConfirmationModal/NewConfirmationModal';
import { toolTipForTable } from '../../../../utils';
import { useGlobalPageSize } from '../../../../utils/tableUtils';
import {
  EVIDENCE_FIELDS, bulkUploadErrorText, choosableRecord, comparisonRows, patientName, reasonIcon, recordRows, sortReasons, candidateTracks,
} from './candidateFields';
import styles from './SheetPatients.module.scss';

const STATES = ['refining', 'proposed', 'unchanged', 'accepted', 'rejected', 'applied', 'error'];
const TIER_OPTIONS = ['mbi', 'mrn', 'name_dob', 'sparse'].map(t => ({ value: t, label: t }));

// fieldLabels are the review's field labels plus the ones the patient pages already define.
const fieldLabels = () => ({
  ...Strings.bulkUploadReview.fields,
  athenaPatientId: Strings.capPatient.athenaPatientId,
  athenaPracticeId: Strings.capPatient.practiceId,
  athenaDepartmentId: Strings.athenaSimDepartment,
  phone: Strings.phone,
  primaryCareProvider: Strings.primaryCareProvider,
});
const reasonText = reason => bulkUploadErrorText(
  reason, Strings.bulkUploadReview.reasons, Strings.bulkUploadReview.sources, fieldLabels(),
);
// ReasonsSummary shows the most important reason (by level) with its icon, "and N more" when
// there are others, and every reason in the tooltip.
export const ReasonsSummary = ({ reasons }) => {
  const sorted = sortReasons(reasons);
  if (sorted.length === 0) return '-';
  const texts = sorted.map(reasonText);
  return (
    <div className="icon-and-text-container">
      <div className={`icon-container ${reasonIcon(sorted[0].level)}`} />
      {toolTipForTable(texts[0], texts)}
      {sorted.length > 1 && (
        <span className={styles.moreCount}>{Strings.formatString(Strings.bulkUploadReview.andMore, sorted.length - 1)}</span>
      )}
    </div>
  );
};

ReasonsSummary.propTypes = {
  reasons: PropTypes.array,
};

const stateText = state => Strings.bulkUploadReview.states[state] || state;
const sourceText = source => Strings.bulkUploadReview.sources[source] || source;
// A candidate can be re-checked (sent back to every source) from these states.
const RECHECK_STATES = ['proposed', 'unchanged', 'error'];
// A person can pick a record for a candidate in these states.
const CHOICE_STATES = ['refining', 'proposed', 'unchanged', 'error'];
// How often the page refreshes while the sheet is processed or sources still check candidates.
const CHECKING_REFRESH_MS = 15000;

// fieldLabelCell renders a comparison row's field label, marked with the shared tick icon when the
// field changed (creates list only new values, so nothing is marked there).
const fieldLabelCell = info => (info.row.original.markChanged ? (
  <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={Strings.bulkUploadReview.changed}>
    <div className="icon-container tick" />
    {info.getValue()}
  </div>
) : info.getValue());

// valueCell renders a before/after value, with the full text in a tooltip (long values truncate).
const valueCell = info => (info.getValue() ? toolTipForTable(info.getValue(), info.getValue()) : '-');

// stateCell shows the candidate state. A candidate waiting for a person to pick the patient is
// marked (it can't be accepted until then), and so is one whose identity is uncertain (it can only
// be accepted by itself, never by a review bar button). A refining one names the sources still
// checking it.
const stateCell = info => {
  const candidate = info.row.original;
  if (candidate.choice_pending) {
    return (
      <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={Strings.bulkUploadReview.choicePendingHint}>
        <div className="icon-container warning" />
        {Strings.bulkUploadReview.choicePending}
      </div>
    );
  }
  if (candidate.state === 'refining') {
    const pending = (candidate.checks || []).filter(c => c.pending).map(c => sourceText(c.source));
    return toolTipForTable(`${stateText(candidate.state)}${pending.length ? ` (${pending.join(', ')})` : ''}`, pending);
  }
  if (candidate.uncertain) {
    return (
      <div className="icon-and-text-container" data-tooltip-id="tooltip" data-tooltip-content={Strings.bulkUploadReview.uncertain}>
        <div className="icon-container warning" />
        {stateText(candidate.state)}
      </div>
    );
  }
  return stateText(candidate.state);
};

// EVIDENCE_ICONS are the shared cell icons a record's per-field comparison with the sheet uses:
// agrees, disagrees, or nothing to compare.
const EVIDENCE_ICONS = { same: 'tick', different: 'alert', unknown: 'tick grey' };

// evidenceCell lists the fields both the record and the sheet have, and whether they agree.
const evidenceCell = info => {
  const evidence = info.getValue() || {};
  const fields = EVIDENCE_FIELDS.filter(f => evidence[f] && evidence[f] !== 'unknown');
  return fields.length > 0 ? (
    <div className={styles.summaryGroup}>
      {fields.map(f => (
        <div
          key={f}
          className="icon-and-text-container"
          data-tooltip-id="tooltip"
          data-tooltip-content={Strings.bulkUploadReview.evidence[evidence[f]]}
        >
          <div className={`icon-container ${EVIDENCE_ICONS[evidence[f]]}`} />
          {fieldLabels()[f] || f}
        </div>
      ))}
    </div>
  ) : '-';
};

// recordCell shows who the record is first (name, date of birth), then what tells records apart.
const recordCell = info => {
  const rows = recordRows(info.getValue(), fieldLabels());
  const byKey = Object.fromEntries(rows.map(r => [r.key, r.value]));
  const who = [`${byKey.firstName || ''} ${byKey.lastName || ''}`.trim(), byKey.dateOfBirth].filter(v => v);
  const rest = rows.filter(r => !['firstName', 'lastName', 'dateOfBirth'].includes(r.key)).map(r => `${r.label}: ${r.value}`);
  const text = [...who, ...rest];
  return text.length > 0 ? toolTipForTable(text.join('; '), rows.map(r => `${r.label}: ${r.value}`)) : '-';
};

// verdictCell is the source's verdict on the record, or that a person chose it.
const verdictCell = info => (info.row.original.chosen ? (
  <div className="icon-and-text-container">
    <div className="icon-container tick" />
    {Strings.bulkUploadReview.chosen}
  </div>
) : toolTipForTable(Strings.bulkUploadReview.verdicts[info.getValue()] || info.getValue()));

// CandidateMatches is the picker: per source (our patients, athenaOne), where its check stands and
// the records it found, with the evidence against the sheet. When several records could be the
// patient, a person picks one, or says it is none of them: for our patients that means "a new
// patient", for athenaOne "not in athenaOne". A choice is re-checked at once with every source.
function CandidateMatches({ matches, canChoose, onChoose }) {
  const columns = React.useMemo(() => [
    columnHelper.accessor('record', {
      header: Strings.bulkUploadReview.record,
      size: 420,
      enableSorting: false,
      cell: recordCell,
    }),
    columnHelper.accessor('evidence', {
      header: Strings.bulkUploadReview.evidenceHeader,
      size: 250,
      enableSorting: false,
      cell: evidenceCell,
    }),
    columnHelper.accessor('verdict', {
      header: Strings.bulkUploadReview.verdict,
      size: 220,
      enableSorting: false,
      cell: verdictCell,
    }),
  ], []);
  if (!matches) return null;
  return (
    <div className={styles.detail}>
      {matches.sources.map(source => {
        // A source that only supplies data (e.g. the PCP from NPPES) didn't look for the patient.
        const outcomes = source.choosable === false ? Strings.bulkUploadReview.dataOutcomes : Strings.bulkUploadReview.outcomes;
        const outcome = source.pending
          ? Strings.bulkUploadReview.sourcePending
          : (outcomes[source.outcome] || source.outcome || '-');
        // A source that only supplies data (e.g. the PCP from NPPES) has nothing to choose.
        const sourceChoosable = canChoose && source.choosable;
        const buttons = sourceChoosable ? [{
          icon: 'activate',
          testId: `choose-${source.source}`,
          text: Strings.bulkUploadReview.thisIsThePatient,
          visible: (rowId, record) => choosableRecord(record) && !record.chosen,
          onClick: (rowId, record) => onChoose(source.source, { record_key: record.key }),
        }] : [];
        return (
          <div key={source.source} className={styles.detail} data-testid={`source[${source.source}]`}>
            <div className={styles.summaryGroup}>
              <span className={styles.summaryItem}>{sourceText(source.source)}</span>
              <span className={styles.chip}>{outcome}</span>
              {source.choice_none && (
                <span className={styles.chip}>{Strings.bulkUploadReview.noneChosen[source.source] || Strings.bulkUploadReview.noneChosen.other}</span>
              )}
            </div>
            {source.records.length > 0 && (
              <Table3
                name="sheet-patient-source-records"
                columns={columns}
                data={source.records}
                buttons={buttons}
                noPagination
                pagination={{ limit: source.records.length }}
              />
            )}
            {sourceChoosable && !source.choice_none && source.records.length > 0 && (
              <div className={styles.summaryGroup}>
                <button
                  className="brand-white-gray"
                  type="button"
                  data-testid={`button[choose-none-${source.source}]`}
                  onClick={() => onChoose(source.source, { none: true })}
                >
                  {Strings.bulkUploadReview.chooseNone[source.source] || Strings.bulkUploadReview.chooseNone.other}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

CandidateMatches.propTypes = {
  matches: PropTypes.object,
  canChoose: PropTypes.bool.isRequired,
  onChoose: PropTypes.func.isRequired,
};

function CandidateComparison({ candidate, onNavigate }) {
  const showBefore = candidate.action !== 'create';
  const rows = React.useMemo(
    // Changed fields first, so an update's changes are visible without scrolling.
    () => comparisonRows(candidate, fieldLabels())
      .map(r => ({ ...r, markChanged: showBefore && r.changed }))
      .sort((a, b) => Number(b.markChanged) - Number(a.markChanged)),
    [candidate, showBefore],
  );
  const reasons = React.useMemo(() => sortReasons(candidate.reasons).map(r => ({ ...r, text: reasonText(r) })), [candidate.reasons]);
  const reasonColumns = React.useMemo(() => [
    columnHelper.accessor('level', {
      header: Strings.bulkUploadReview.level,
      size: 110,
      enableSorting: false,
      cell: info => (
        <div className="icon-and-text-container">
          <div className={`icon-container ${reasonIcon(info.getValue())}`} />
          {Strings.bulkUploadReview.levels[info.getValue()] || info.getValue()}
        </div>
      ),
    }),
    columnHelper.accessor('text', {
      header: Strings.bulkUploadReview.issues,
      size: 510,
      enableSorting: false,
      cell: valueCell,
    }),
  ], []);
  const columns = React.useMemo(() => [
    columnHelper.accessor('label', {
      header: Strings.bulkUploadReview.field,
      size: 150,
      enableSorting: false,
      cell: fieldLabelCell,
    }),
    ...(showBefore ? [columnHelper.accessor('before', {
      header: Strings.bulkUploadReview.before,
      size: 155,
      enableSorting: false,
      cell: valueCell,
    })] : []),
    columnHelper.accessor('after', {
      header: candidate.state === 'applied' ? Strings.bulkUploadReview.after : Strings.bulkUploadReview.requested,
      size: showBefore ? 155 : 310,
      enableSorting: false,
      cell: valueCell,
    }),
  ], [showBefore, candidate.state]);

  return (
    <div className={styles.detail}>
      <div className={styles.summaryGroup}>
        <span className={styles.chip}>
          {`${Strings.bulkUploadReview.action}: ${candidate.action ? Strings.bulkUploadReview.actions[candidate.action] : '-'}`}
        </span>
        <span className={styles.chip}>{`${Strings.bulkUploadReview.state}: ${stateText(candidate.state)}`}</span>
        {candidate.uncertain && <span className={styles.chip}>{Strings.bulkUploadReview.uncertain}</span>}
        {candidate.reviewed_at && <span className={styles.chip}>{`${Strings.bulkUploadReview.reviewed}: ${candidate.reviewed_at}`}</span>}
        {candidate.reasons?.filter(r => r.owner_patient_id).map(r => (
          <button
            key={r.id}
            className="link-button"
            type="button"
            data-testid={`button[open-owner-${r.id}]`}
            onClick={() => onNavigate(`/cap-patients/${r.owner_patient_id}`)}
          >
            {Strings.bulkUploadReview.openOwner[r.id] || Strings.bulkUploadReview.openPatient}
          </button>
        ))}
      </div>
      {reasons.length > 0 && (
        <Table3
          name="sheet-patient-reasons-table"
          columns={reasonColumns}
          data={reasons}
          noPagination
          pagination={{ limit: reasons.length }}
        />
      )}
      <Table3
        name="sheet-patient-comparison-table"
        columns={columns}
        data={rows}
        noPagination
        pagination={{ limit: rows.length }}
      />
    </div>
  );
}

CandidateComparison.propTypes = {
  candidate: PropTypes.object.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

function SheetPatients(props) {
  const {
    sheetId, patientSummary, processing, getSheetPatients, decideSheetPatients, onDecided, onNavigate, showNotification,
    openConfirmModal, getCandidateMatches, chooseCandidateMatch, retryCandidate,
  } = props;
  const globalPageSize = useGlobalPageSize();
  const [patients, setPatients] = React.useState([]);
  const [pagination, setPagination] = React.useState({ offset: 0, limit: globalPageSize, totalRecords: 0 });
  const [filters, setFilters] = React.useState({ state: [], tier: [], action: [] });
  const [selectedPatient, setSelectedPatient] = React.useState(null);
  const [matches, setMatches] = React.useState(null);
  const pageRequest = React.useRef({ offset: 0, limit: globalPageSize, state: [], tier: [], action: [] });
  const stateOptions = React.useMemo(() => STATES.map(s => ({ value: s, label: stateText(s) })), []);
  const actionOptions = React.useMemo(
    () => ['create', 'update'].map(a => ({ value: a, label: Strings.bulkUploadReview.actions[a] })),
    [],
  );

  const load = React.useCallback(() => {
    const params = { offset: pageRequest.current.offset, limit: pageRequest.current.limit };
    if (pageRequest.current.state.length > 0) params.state = pageRequest.current.state;
    if (pageRequest.current.tier.length > 0) params.tier = pageRequest.current.tier;
    if (pageRequest.current.action.length > 0) params.action = pageRequest.current.action;
    return getSheetPatients(sheetId, params).then(r => {
      if (r?.type !== BULK_UPLOAD_GET_SHEET_PATIENTS_RESULT) return [];
      // name and target_org_name are flattened into plain keys so Table3 keeps the column order.
      const rows = (r?.response?.data || []).map(c => ({
        ...c,
        name: patientName(c),
        target_org_name: c.target_org?.name,
      }));
      setPatients(rows);
      setPagination(r?.response?.pagination || { offset: 0, limit: pageRequest.current.limit, totalRecords: 0 });
      return rows;
    });
  }, [sheetId, getSheetPatients]);

  // The picker of the open candidate.
  React.useEffect(() => {
    setMatches(null);
    if (!selectedPatient) return;
    getCandidateMatches(sheetId, selectedPatient.candidate_id).then(r => {
      if (r?.type === BULK_UPLOAD_GET_CANDIDATE_MATCHES_RESULT) setMatches(r.response);
    });
  }, [sheetId, selectedPatient, getCandidateMatches]);

  // afterRecheck refreshes the list and the open candidate after a choice or a re-check, which the
  // backend runs at once (as far as the sources answer within the request).
  const afterRecheck = React.useCallback((r, okType) => {
    if (r?.type !== okType) {
      showNotification(r?.response?.data?.message || Strings.error, 0, true);
      return;
    }
    setMatches(r.response);
    load().then(rows => {
      const updated = rows.find(c => c.candidate_id === r.response.candidate_id);
      setSelectedPatient(prev => updated || (prev && { ...prev, state: r.response.state, choice_pending: r.response.choice_pending }));
    });
    onDecided();
  }, [load, onDecided, showNotification]);

  const choose = React.useCallback((source, data) => {
    const send = () => chooseCandidateMatch(sheetId, selectedPatient.candidate_id, { source, ...data })
      .then(r => afterRecheck(r, BULK_UPLOAD_CHOOSE_CANDIDATE_MATCH_RESULT));
    if (data.none && source === 'local') {
      openConfirmModal({ text: Strings.bulkUploadReview.confirmCreateNew, onConfirm: send });
      return;
    }
    send();
  }, [sheetId, selectedPatient, chooseCandidateMatch, afterRecheck, openConfirmModal]);

  const recheck = React.useCallback(() => {
    retryCandidate(sheetId, selectedPatient.candidate_id).then(r => afterRecheck(r, BULK_UPLOAD_RETRY_CANDIDATE_RESULT));
  }, [sheetId, selectedPatient, retryCandidate, afterRecheck]);

  // The summary (which also says whether decisions are possible) is refreshed together with the
  // list: planning may have finished since the sheet page loaded it.
  const refreshSummary = React.useRef(onDecided);
  refreshSummary.current = onDecided;
  React.useEffect(() => {
    load();
    refreshSummary.current();
  }, [load]);

  // While the sheet is being processed, or sources still check candidates (e.g. athenaOne), the
  // list and the summary catch up by themselves.
  const checking = processing || Object.keys(patientSummary?.pending_checks_by_source || {}).length > 0;
  React.useEffect(() => {
    if (!checking) return undefined;
    const timer = setInterval(() => {
      load();
      refreshSummary.current();
    }, CHECKING_REFRESH_MS);
    return () => clearInterval(timer);
  }, [checking, load]);

  const onOffsetChange = React.useCallback((offset, limit) => {
    if (isNaN(offset)) return;
    pageRequest.current.offset = offset;
    if (limit) pageRequest.current.limit = limit;
    load();
  }, [load]);

  const onPageSizeChange = React.useCallback(limit => {
    pageRequest.current.limit = limit;
    pageRequest.current.offset = 0;
    load();
  }, [load]);

  const onFilterChange = React.useCallback((key, values) => {
    pageRequest.current[key] = values || [];
    pageRequest.current.offset = 0;
    setFilters(prev => ({ ...prev, [key]: values || [] }));
    load();
  }, [load]);

  // decide sends one decision and reports what it did. Accepted patients are written by the
  // background job, which sends any that changed since planning back for review.
  const decide = React.useCallback(body => {
    decideSheetPatients(sheetId, body).then(r => {
      if (r?.type !== BULK_UPLOAD_DECIDE_SHEET_PATIENTS_RESULT) {
        showNotification(r?.response?.data?.message || Strings.error, 0, true);
        return;
      }
      const { accepted, rejected } = r.response;
      const messages = [Strings.formatString(Strings.bulkUploadReview.decisionDone, accepted, rejected)];
      if (accepted > 0) messages.push(Strings.bulkUploadReview.decisionAppliedLater);
      showNotification(messages.join(' '));
      setSelectedPatient(null);
      load();
      onDecided();
    });
  }, [sheetId, decideSheetPatients, showNotification, load, onDecided]);

  const confirmDecision = React.useCallback((text, body) => openConfirmModal({
    text,
    onConfirm: () => decide(body),
  }), [openConfirmModal, decide]);

  const issueCell = React.useCallback(row => <ReasonsSummary reasons={row.original.reasons} />, []);

  const columns = React.useMemo(() => [
    columnHelper.accessor('source_rows', {
      header: Strings.bulkUploadReview.sourceRows,
      size: 110,
      cell: info => info.getValue()?.join(', ') || '-',
    }),
    columnHelper.accessor('name', {
      header: Strings.bulkUploadReview.name,
      size: 180,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('action', {
      header: Strings.bulkUploadReview.action,
      size: 100,
      cell: info => (info.getValue() ? Strings.bulkUploadReview.actions[info.getValue()] : '-'),
    }),
    columnHelper.accessor('state', {
      header: Strings.bulkUploadReview.state,
      size: 170,
      cell: stateCell,
    }),
    columnHelper.accessor('tier', {
      header: Strings.bulkUploadReview.tier,
      size: 100,
    }),
    columnHelper.accessor('target_org_name', {
      header: Strings.bulkUploadReview.targetOrg,
      size: 180,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor(row => candidateTracks(row), {
      id: 'track',
      header: Strings.bulkUploadReview.track,
      size: 130,
      enableSorting: false,
      cell: info => info.getValue() || '-',
    }),
    columnHelper.accessor('changed_fields', {
      header: Strings.bulkUploadReview.changedFields,
      size: 220,
      cell: info => {
        const fields = (info.getValue() || []).map(f => fieldLabels()[f] || f);
        return fields.length > 0 ? toolTipForTable(fields.join(', '), fields) : '-';
      },
    }),
    columnHelper.accessor('reasons', {
      header: Strings.bulkUploadReview.issues,
      size: 200,
      cell: info => issueCell(info.row),
    }),
  ], [issueCell]);

  // Decisions stay possible while accepted patients are being applied, so the decision controls
  // follow the backend's can_decide rather than the sheet status.
  const canDecide = !!patientSummary?.can_decide;
  const buttons = React.useMemo(() => {
    const isProposed = (rowId, data) => canDecide && data.state === 'proposed';
    const isAcceptable = (rowId, data) => isProposed(rowId, data) && !data.choice_pending;
    return [{
      icon: 'preview',
      text: Strings.bulkUploadReview.details,
      onClick: (rowId, data) => setSelectedPatient(data),
    }, {
      icon: 'activate',
      testId: 'accept',
      text: Strings.bulkUploadReview.accept,
      visible: isAcceptable,
      onClick: (rowId, data) => decide({ decision: 'accept', candidate_ids: [data.candidate_id] }),
    }, {
      icon: 'cancelEnrollment',
      testId: 'reject',
      text: Strings.bulkUploadReview.reject,
      visible: isProposed,
      onClick: (rowId, data) => decide({ decision: 'reject', candidate_ids: [data.candidate_id] }),
    }];
  }, [decide, canDecide]);

  const selectedPatientId = selectedPatient?.after?.patientId || selectedPatient?.before?.patientId;
  const summary = patientSummary || {};
  const stateCounts = summary.counts_by_state || {};
  const tierCounts = summary.counts_by_tier || {};
  const certainUpdates = summary.proposed_certain_updates || 0;
  const certainCreates = summary.proposed_certain_creates || 0;
  const proposed = stateCounts.proposed || 0;
  const uncertainNote = summary.proposed_uncertain > 0 ? ` ${Strings.bulkUploadReview.uncertainNotIncluded}` : '';
  const pendingChecks = summary.pending_checks_by_source || {};
  const canChoose = canDecide && !!selectedPatient && CHOICE_STATES.includes(selectedPatient.state);

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        <div className={styles.summary}>
          <span className={styles.summaryItem}>
            {`${Strings.bulkUploadReview.total}: ${summary.total ?? '-'}`}
          </span>
          <span className={styles.summaryGroup}>
            {Object.keys(stateCounts).map(s => (
              <span key={s} className={styles.chip}>{`${stateText(s)}: ${stateCounts[s]}`}</span>
            ))}
          </span>
          <span className={styles.summaryGroup}>
            {Object.keys(tierCounts).map(t => (
              <span key={t} className={styles.chip}>{`${t}: ${tierCounts[t]}`}</span>
            ))}
          </span>
          {Object.keys(pendingChecks).length > 0 && (
            <span className={styles.summaryGroup} data-testid="pending-checks">
              {Object.keys(pendingChecks).map(source => (
                <span key={source} className={styles.chip}>
                  {Strings.formatString(Strings.bulkUploadReview.stillChecking, sourceText(source), pendingChecks[source])}
                </span>
              ))}
            </span>
          )}
          {summary.proposed_choice_pending > 0 && (
            <span className={styles.chip}>{Strings.formatString(Strings.bulkUploadReview.choicesWaiting, summary.proposed_choice_pending)}</span>
          )}
        </div>
        <div className={styles.filters}>
          <Multiselect
            id="sheet-patients-state-filter"
            label={Strings.bulkUploadReview.filterState}
            placeholder={Strings.bulkUploadReview.filterState}
            data={stateOptions}
            value={filters.state}
            onChange={e => onFilterChange('state', e?.map(o => o.value) || [])}
          />
          <Multiselect
            id="sheet-patients-tier-filter"
            label={Strings.bulkUploadReview.filterTier}
            placeholder={Strings.bulkUploadReview.filterTier}
            data={TIER_OPTIONS}
            value={filters.tier}
            onChange={e => onFilterChange('tier', e?.map(o => o.value) || [])}
          />
          <Multiselect
            id="sheet-patients-action-filter"
            label={Strings.bulkUploadReview.filterAction}
            placeholder={Strings.bulkUploadReview.filterAction}
            data={actionOptions}
            value={filters.action}
            onChange={e => onFilterChange('action', e?.map(o => o.value) || [])}
          />
        </div>
      </div>
      {canDecide && proposed > 0 && (
        <div className={styles.summaryGroup}>
          <button
            className="brand-blue"
            type="button"
            data-testid="button[accept-matched]"
            disabled={certainUpdates === 0}
            onClick={() => confirmDecision(
              Strings.formatString(Strings.bulkUploadReview.confirmAcceptMatched, certainUpdates) + uncertainNote,
              { decision: 'accept', filter: { action: ['update'] } },
            )}
          >
            {Strings.formatString(Strings.bulkUploadReview.acceptMatched, certainUpdates)}
          </button>
          <button
            className="brand-blue"
            type="button"
            data-testid="button[accept-new]"
            disabled={certainCreates === 0}
            onClick={() => confirmDecision(
              Strings.formatString(Strings.bulkUploadReview.confirmAcceptNew, certainCreates) + uncertainNote,
              { decision: 'accept', filter: { action: ['create'] } },
            )}
          >
            {Strings.formatString(Strings.bulkUploadReview.acceptNew, certainCreates)}
          </button>
          <button
            className="brand-white-gray"
            type="button"
            data-testid="button[reject-remaining]"
            onClick={() => confirmDecision(
              Strings.formatString(Strings.bulkUploadReview.confirmRejectRemaining, proposed),
              { decision: 'reject', filter: {} },
            )}
          >
            {Strings.formatString(Strings.bulkUploadReview.rejectRemaining, proposed)}
          </button>
        </div>
      )}
      <Table3
        name="sheet-patients-table"
        columns={columns}
        data={patients}
        buttons={buttons}
        pagination={pagination}
        onOffsetChange={onOffsetChange}
        onPageSizeChange={onPageSizeChange}
      />
      {selectedPatient && (
        <SimpleModal
          name="sheet-patient-detail-modal"
          className={styles.wideModal}
          title={`${Strings.bulkUploadReview.patientPreview}: ${patientName(selectedPatient) || '-'}`}
          onCancel={() => setSelectedPatient(null)}
          customButtons={(
            <div className="buttons center" key="button-bar">
              <div className="bottom-button-container">
                <button className="brand-white-gray" type="button" onClick={() => setSelectedPatient(null)}>
                  {Strings.cancel}
                </button>
                {canDecide && RECHECK_STATES.includes(selectedPatient.state) && (
                  <button
                    className="brand-white-gray"
                    type="button"
                    data-testid="button[recheck-patient]"
                    onClick={recheck}
                  >
                    {Strings.bulkUploadReview.recheck}
                  </button>
                )}
                {canDecide && selectedPatient.state === 'proposed' && (
                  <>
                    <button
                      className="brand-white-gray"
                      type="button"
                      data-testid="button[reject-patient]"
                      onClick={() => decide({ decision: 'reject', candidate_ids: [selectedPatient.candidate_id] })}
                    >
                      {Strings.bulkUploadReview.reject}
                    </button>
                    <button
                      className="brand-blue"
                      type="button"
                      data-testid="button[accept-patient]"
                      disabled={selectedPatient.choice_pending}
                      title={selectedPatient.choice_pending ? Strings.bulkUploadReview.choicePendingHint : undefined}
                      onClick={() => decide({ decision: 'accept', candidate_ids: [selectedPatient.candidate_id] })}
                    >
                      {Strings.bulkUploadReview.accept}
                    </button>
                  </>
                )}
                {selectedPatientId && (
                  <button
                    className="brand-blue"
                    type="button"
                    data-testid="button[open-patient]"
                    onClick={() => onNavigate(`/cap-patients/${selectedPatientId}`)}
                  >
                    {Strings.bulkUploadReview.openPatient}
                  </button>
                )}
              </div>
            </div>
          )}
          content={(
            <>
              <CandidateComparison candidate={selectedPatient} onNavigate={onNavigate} />
              <CandidateMatches matches={matches} canChoose={canChoose} onChoose={choose} />
            </>
          )}
        />
      )}
    </div>
  );
}

SheetPatients.propTypes = {
  sheetId: PropTypes.string.isRequired,
  patientSummary: PropTypes.object,
  processing: PropTypes.bool,
  onDecided: PropTypes.func.isRequired,
  getSheetPatients: PropTypes.func.isRequired,
  decideSheetPatients: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
  openConfirmModal: PropTypes.func.isRequired,
  getCandidateMatches: PropTypes.func.isRequired,
  chooseCandidateMatch: PropTypes.func.isRequired,
  retryCandidate: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  getSheetPatients: bulkUploadActions.getSheetPatients,
  decideSheetPatients: bulkUploadActions.decideSheetPatients,
  getCandidateMatches: bulkUploadActions.getCandidateMatches,
  chooseCandidateMatch: bulkUploadActions.chooseCandidateMatch,
  retryCandidate: bulkUploadActions.retryCandidate,
  onNavigate: path => push(path),
  showNotification: (message, timeout, error) => notificationActions.show(message, timeout, error),
  openConfirmModal: data => openModalAction(NewConfirmationModal.MODAL_NAME, data),
};

export default connect(null, mapDispatchToProps)(SheetPatients);
